import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {View, Text, ScrollView, TouchableOpacity, Linking} from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';
import {useDispatch, useSelector} from 'react-redux';
import Toast, {useToast} from '../../commons/Toast';
import {
  fetchCostCalculator,
  submitCostCalculation
} from '../../actions/costCalculatorActions';
import {
  DEFAULT_COST_CALCULATOR_FIELDS,
  DEFAULT_COMPUTED_SUMMARY,
  calculateCostSummary,
  deriveLinkedFieldUpdates
} from '../../utils/costCalculatorUtils';
import NumberField from './NumberField';
import TextField from './TextField';
import SectionHeader from './SectionHeader';
import styles from '../../style/costCalculator';

const DEMO_URL = 'https://www.groovepacker.com/see-a-demo';
const PRICING_URL = 'https://www.groovepacker.com/pricing';

const formatCurrency = (value) => {
  const num = Number(value) || 0;
  return `$${num.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
};

/**
 * `apiUrlOverride`: passed by the public, unauthenticated entry point
 * (App.js → isPublicCostCalculatorRoute() → PUBLIC_COST_CALCULATOR_API_URL) so this screen
 * doesn't depend on a logged-in tenant's AsyncStorage-derived URL. Omitted when reached the
 * normal way (via Settings, while logged in), in which case the actions fall back to the
 * per-tenant URL as before.
 */
const CostCalculatorScreen = ({apiUrlOverride} = {}) => {
  const dispatch = useDispatch();
  const {status, error, submitStatus, submitError} = useSelector(
    (state) => state.costCalculator
  );

  // `fields` holds every primitive input the user can edit. `computed` holds the derived
  // totals (total_cost, monthly_shipping, gp_cost, monthly_saving, and every intermediate).
  // On initial load these are seeded VERBATIM from the GET response (which is what makes
  // "with all defaults, total_cost=17" true out of the box — see costCalculatorUtils.js for
  // why that must be a pass-through rather than a fresh recompute). From the first edit
  // onward, `computed` is always freshly derived via `calculateCostSummary`.
  const [fields, setFields] = useState(DEFAULT_COST_CALCULATOR_FIELDS);
  const [computed, setComputed] = useState(DEFAULT_COMPUTED_SUMMARY);
  const [hasEdited, setHasEdited] = useState(false);

  const [recipientName, setRecipientName] = useState('');
  const [recipients, setRecipients] = useState(['', '', '']);
  const [emailText, setEmailText] = useState('');
  const [followUpEmail, setFollowUpEmail] = useState(false);

  const {showToast, toastMessage, showToastMessage, hideToast} = useToast();

  const loadCalculator = useCallback(async () => {
    const result = await dispatch(fetchCostCalculator(apiUrlOverride));
    if (result.success && result.data) {
      const {data} = result;
      const nextFields = {};
      Object.keys(DEFAULT_COST_CALCULATOR_FIELDS).forEach((key) => {
        nextFields[key] = data[key] !== undefined && data[key] !== null
          ? data[key]
          : DEFAULT_COST_CALCULATOR_FIELDS[key];
      });
      const nextComputed = {};
      Object.keys(DEFAULT_COMPUTED_SUMMARY).forEach((key) => {
        nextComputed[key] = data[key] !== undefined && data[key] !== null
          ? Number(data[key])
          : DEFAULT_COMPUTED_SUMMARY[key];
      });
      setFields(nextFields);
      setComputed(nextComputed);
      setHasEdited(false);
    }
  }, [dispatch, apiUrlOverride]);

  useEffect(() => {
    loadCalculator();
  }, [loadCalculator]);

  const handleFieldChange = useCallback((fieldName, rawValue) => {
    const value = rawValue === '' ? '' : rawValue;
    setFields((prevFields) => {
      const updates = deriveLinkedFieldUpdates(fieldName, value === '' ? 0 : value);
      return {...prevFields, ...updates, [fieldName]: value};
    });
    setHasEdited(true);
  }, []);

  // Recompute the derived totals whenever any primitive input changes, but only once the
  // user has actually touched something — before that, `computed` stays the verbatim
  // GET-response snapshot (see the comment on `computed`'s useState above).
  useEffect(() => {
    if (!hasEdited) return;
    setComputed(calculateCostSummary(fields));
  }, [fields, hasEdited]);

  const handleRecipientChange = (index, value) => {
    setRecipients((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const validRecipients = useMemo(
    () => recipients.filter((email) => email && email.includes('@')),
    [recipients]
  );

  const handleSend = async () => {
    if (validRecipients.length === 0) {
      showToastMessage('Add at least one valid email address before sending.');
      return;
    }
    const result = await dispatch(
      submitCostCalculation({
        fields,
        computed,
        recipients,
        recipientName,
        emailText,
        followUpEmail,
        onlySave: false,
        apiUrlOverride
      })
    );
    if (result.success) {
      showToastMessage(result.data?.message || 'Sent!');
    } else {
      showToastMessage(result.error || 'Something went wrong sending this — try again.');
    }
  };

  const openExternal = (url) => {
    Linking.openURL(url);
  };

  if (status === 'pending' && !hasEdited) {
    return (
      <View style={[styles.screen, {alignItems: 'center', justifyContent: 'center'}]}>
        <LinearGradient
          colors={['#111C29', '#253C57', '#5C778F', '#253C57', '#111B27']}
          style={{flex: 1, width: '100%', alignItems: 'center', justifyContent: 'center'}}
        >
          <Text style={styles.loadingText}>Loading the cost calculator…</Text>
        </LinearGradient>
      </View>
    );
  }

  return (
    <LinearGradient
      colors={['#111C29', '#253C57', '#5C778F', '#5C778F', '#253C57', '#111B27']}
      locations={[0.09, 0.19, 0.43, 0.58, 0.78, 0.92]}
      start={{x: 0, y: 0}}
      end={{x: 1, y: 1}}
      style={styles.screen}
    >
      <ScrollView contentContainerStyle={styles.scrollContent}>
        <View style={styles.panel}>
          <Text style={styles.headline}>Fulfillment Error Cost Calculator</Text>
          <Text style={styles.subhead}>
            See what shipping errors are really costing your business every month — and what
            GroovePacker could save you.
          </Text>
          <View style={styles.heroDivider} />

          {status === 'failed' && (
            <View style={styles.errorBanner}>
              <Text style={styles.errorBannerText}>
                {error?.message || 'Could not load the calculator. Showing defaults instead.'}
              </Text>
              <TouchableOpacity style={styles.retryButton} onPress={loadCalculator} testID="retryLoadButton">
                <Text style={styles.retryButtonText}>Retry</Text>
              </TouchableOpacity>
            </View>
          )}

          {/* ─── Error Volume ─────────────────────────────────────────── */}
          <View style={styles.section}>
            <SectionHeader
              number="1"
              title="Error Volume"
              description="Start with your packing team and order volume — this tells us how many fulfillment errors you're likely shipping every day."
            />
            <View style={styles.fieldRow}>
              <NumberField
                testID="packer_count"
                label="Number of Packers"
                value={fields.packer_count}
                onChangeValue={(v) => handleFieldChange('packer_count', v)}
              />
              <NumberField
                testID="order_count"
                label="Orders Packed per User per Day"
                value={fields.order_count}
                onChangeValue={(v) => handleFieldChange('order_count', v)}
              />
              <NumberField
                testID="avg_error"
                label="Average Error Rate %"
                value={fields.avg_error}
                onChangeValue={(v) => handleFieldChange('avg_error', v)}
              />
            </View>
            <View style={styles.costBox}>
              <Text style={styles.costBoxLabel}>Estimated errors per day</Text>
              <Text style={styles.costBoxValue}>{Number(computed.error_per_day).toFixed(2)}</Text>
            </View>
          </View>

          {/* ─── Tangible Costs ───────────────────────────────────────── */}
          <View style={styles.section}>
            <SectionHeader
              number="2"
              title="Tangible Costs"
              description="The hard, out-of-pocket costs each fulfillment error creates — support time, reshipments, and returns or abandoned product."
            />

            <View style={styles.subsectionCard}>
              <SectionHeader
                size="sub"
                number="2a"
                title="Communication & Customer Service Costs"
                description="What it costs to field and resolve a customer contact about the error."
              />
              <View style={styles.fieldRow}>
              <NumberField
                testID="regular_percentage"
                label="Regular Contact %"
                value={fields.regular_percentage}
                onChangeValue={(v) => handleFieldChange('regular_percentage', v)}
                hint="Linked to Escalated Contact % (they always add to 100%)"
              />
              <NumberField
                testID="escalated_percentage"
                label="Escalated Contact %"
                value={fields.escalated_percentage}
                onChangeValue={(v) => handleFieldChange('escalated_percentage', v)}
              />
              <NumberField
                testID="regular_comm"
                label="Cost per Regular Contact"
                isCurrency
                value={fields.regular_comm}
                onChangeValue={(v) => handleFieldChange('regular_comm', v)}
              />
              <NumberField
                testID="escalated_comm"
                label="Cost per Escalated Contact"
                isCurrency
                value={fields.escalated_comm}
                onChangeValue={(v) => handleFieldChange('escalated_comm', v)}
              />
              <NumberField
                testID="avg_comm"
                label="Avg Contacts per Error"
                value={fields.avg_comm}
                onChangeValue={(v) => handleFieldChange('avg_comm', v)}
              />
            </View>
              <View style={styles.costBox}>
                <Text style={styles.costBoxLabel}>Communication &amp; customer service cost per error</Text>
                <Text style={styles.costBoxValue}>{formatCurrency(computed.cost_of_comm)}</Text>
              </View>
            </View>

            <View style={styles.subsectionCard}>
              <SectionHeader
                size="sub"
                number="2b"
                title="Replacement Shipment Costs"
                description="What it costs to ship a corrected order — including expedited and international cases."
              />
              <View style={styles.fieldRow}>
              <NumberField
                testID="cost_ship_replacement"
                label="Cost to Ship Replacement"
                isCurrency
                value={fields.cost_ship_replacement}
                onChangeValue={(v) => handleFieldChange('cost_ship_replacement', v)}
              />
              <NumberField
                testID="reshipment"
                label="Cost of Reshipment"
                isCurrency
                value={fields.reshipment}
                onChangeValue={(v) => handleFieldChange('reshipment', v)}
              />
              <NumberField
                testID="cost_labor_reshipment"
                label="Labor Cost of Reshipment"
                isCurrency
                value={fields.cost_labor_reshipment}
                onChangeValue={(v) => handleFieldChange('cost_labor_reshipment', v)}
              />
              <NumberField
                testID="cost_apology"
                label="Cost of Apology"
                isCurrency
                value={fields.cost_apology}
                onChangeValue={(v) => handleFieldChange('cost_apology', v)}
              />
              <NumberField
                testID="expedited_percentage"
                label="% Requiring Expedited Shipping"
                value={fields.expedited_percentage}
                onChangeValue={(v) => handleFieldChange('expedited_percentage', v)}
                hint="Linked to '1 in every N orders' below"
              />
              <NumberField
                testID="expedited_count"
                label="1 in every N Orders"
                value={fields.expedited_count}
                onChangeValue={(v) => handleFieldChange('expedited_count', v)}
              />
              <NumberField
                testID="expedited_avg"
                label="Avg Expedited Shipping Cost"
                isCurrency
                value={fields.expedited_avg}
                onChangeValue={(v) => handleFieldChange('expedited_avg', v)}
              />
              <NumberField
                testID="international_percentage"
                label="% International"
                value={fields.international_percentage}
                onChangeValue={(v) => handleFieldChange('international_percentage', v)}
                hint="Linked to '1 in every N orders' below"
              />
              <NumberField
                testID="international_count"
                label="1 in every N Orders (Int'l)"
                value={fields.international_count}
                onChangeValue={(v) => handleFieldChange('international_count', v)}
              />
              <NumberField
                testID="avg_order_profit"
                label="Avg Order Profit"
                isCurrency
                value={fields.avg_order_profit}
                onChangeValue={(v) => handleFieldChange('avg_order_profit', v)}
              />
            </View>
              <View style={styles.costBox}>
                <Text style={styles.costBoxLabel}>Replacement shipment cost per error</Text>
                <Text style={styles.costBoxValue}>{formatCurrency(computed.total_replacement_costs)}</Text>
              </View>
            </View>

            <View style={styles.subsectionCard}>
              <SectionHeader
                size="sub"
                number="2c"
                title="Return Shipment OR Product Abandonment"
                description="Whether the customer sends the wrong item back or simply keeps it, this is what that resolution costs."
              />
              <View style={styles.fieldRow}>
              <NumberField
                testID="total_error_shipment"
                label="% of Errors Resulting in Return/Abandonment"
                value={fields.total_error_shipment}
                onChangeValue={(v) => handleFieldChange('total_error_shipment', v)}
              />
              <NumberField
                testID="return_shipping_percentage"
                label="% Returned"
                value={fields.return_shipping_percentage}
                onChangeValue={(v) => handleFieldChange('return_shipping_percentage', v)}
                hint="Linked to Product Abandonment % (they always add to 100%)"
              />
              <NumberField
                testID="product_abandonment_percentage"
                label="% Abandoned"
                value={fields.product_abandonment_percentage}
                onChangeValue={(v) => handleFieldChange('product_abandonment_percentage', v)}
              />
              <NumberField
                testID="avg_product_abandonment"
                label="Avg Cost of Abandoned Product"
                isCurrency
                value={fields.avg_product_abandonment}
                onChangeValue={(v) => handleFieldChange('avg_product_abandonment', v)}
              />
              <NumberField
                testID="cost_return"
                label="Cost to Process Return"
                isCurrency
                value={fields.cost_return}
                onChangeValue={(v) => handleFieldChange('cost_return', v)}
              />
              <NumberField
                testID="return_shipping_cost"
                label="Return Shipping Cost"
                isCurrency
                value={fields.return_shipping_cost}
                onChangeValue={(v) => handleFieldChange('return_shipping_cost', v)}
              />
              <NumberField
                testID="return_shipping_insurance"
                label="Return Shipping Insurance"
                isCurrency
                value={fields.return_shipping_insurance}
                onChangeValue={(v) => handleFieldChange('return_shipping_insurance', v)}
              />
              <NumberField
                testID="cost_recieving_process"
                label="Cost to Process Receiving"
                isCurrency
                value={fields.cost_recieving_process}
                onChangeValue={(v) => handleFieldChange('cost_recieving_process', v)}
              />
              <NumberField
                testID="cost_confirm"
                label="Cost to Confirm"
                isCurrency
                value={fields.cost_confirm}
                onChangeValue={(v) => handleFieldChange('cost_confirm', v)}
              />
            </View>
              <View style={styles.costBox}>
                <Text style={styles.costBoxLabel}>Return/abandonment cost per error</Text>
                <Text style={styles.costBoxValue}>{formatCurrency(computed.return_shipment_or_abandonment)}</Text>
              </View>
            </View>
          </View>

          {/* ─── Intangible Costs ─────────────────────────────────────── */}
          <View style={styles.section}>
            <SectionHeader
              number="3"
              title="Intangible Costs"
              description="The harder-to-measure fallout from a shipping error — cancelled orders, lost customers, reputation, and inventory shortages."
            />
            <View style={styles.fieldRow}>
              <NumberField
                testID="misc_cost"
                label="Misc. Cost per Error"
                isCurrency
                value={fields.misc_cost}
                onChangeValue={(v) => handleFieldChange('misc_cost', v)}
              />
              <NumberField
                testID="incorrect_current_order"
                label="1 in N Orders With Errors Get Canceled"
                description="Orders that ultimately cancel after receiving an error — customers who receive a mistake and don't reorder."
                value={fields.incorrect_current_order}
                onChangeValue={(v) => handleFieldChange('incorrect_current_order', v)}
                hint="Linked to the % field below"
              />
              <NumberField
                testID="incorrect_current_order_per"
                label="% of Orders Cancelled"
                value={fields.incorrect_current_order_per}
                onChangeValue={(v) => handleFieldChange('incorrect_current_order_per', v)}
              />
              <NumberField
                testID="avg_current_order"
                label="Avg Order Value"
                isCurrency
                value={fields.avg_current_order}
                onChangeValue={(v) => handleFieldChange('avg_current_order', v)}
              />
              <NumberField
                testID="incorrect_lifetime_order"
                label="1 in every N Customers Lost"
                value={fields.incorrect_lifetime_order}
                onChangeValue={(v) => handleFieldChange('incorrect_lifetime_order', v)}
                hint="Linked to the % field below"
              />
              <NumberField
                testID="incorrect_lifetime_order_per"
                label="% of Customers Lost"
                value={fields.incorrect_lifetime_order_per}
                onChangeValue={(v) => handleFieldChange('incorrect_lifetime_order_per', v)}
              />
              <NumberField
                testID="lifetime_val"
                label="Avg Customer Lifetime Value"
                isCurrency
                value={fields.lifetime_val}
                onChangeValue={(v) => handleFieldChange('lifetime_val', v)}
              />
              <NumberField
                testID="negative_shipment"
                label="1 in every N Leaves Negative Review"
                value={fields.negative_shipment}
                onChangeValue={(v) => handleFieldChange('negative_shipment', v)}
                hint="Linked to the % field below"
              />
              <NumberField
                testID="negative_shipment_per"
                label="% Leaving Negative Review"
                value={fields.negative_shipment_per}
                onChangeValue={(v) => handleFieldChange('negative_shipment_per', v)}
              />
              <NumberField
                testID="inventory_shortage_order"
                label="1 in every N Has Inventory Shortage"
                value={fields.inventory_shortage_order}
                onChangeValue={(v) => handleFieldChange('inventory_shortage_order', v)}
                hint="Linked to the % field below"
              />
              <NumberField
                testID="inventory_shortage_order_per"
                label="% With Inventory Shortage"
                value={fields.inventory_shortage_order_per}
                onChangeValue={(v) => handleFieldChange('inventory_shortage_order_per', v)}
              />
            </View>
            <View style={styles.costBox}>
              <Text style={styles.costBoxLabel}>Intangible cost per error</Text>
              <Text style={styles.costBoxValue}>{formatCurrency(computed.intangible_cost)}</Text>
            </View>
          </View>

          {/* ─── Totals ───────────────────────────────────────────────── */}
          <View style={styles.section}>
            <SectionHeader
              number="4"
              title="Totals"
              description="Your bottom line — total cost per error, monthly impact, and what GroovePacker could save you after its own cost."
            />
            <View style={styles.fieldRow}>
              <NumberField
                testID="gp_user_price"
                label="GroovePacker User Price"
                isCurrency
                value={fields.gp_user_price}
                onChangeValue={(v) => handleFieldChange('gp_user_price', v)}
              />
            </View>
            <View style={styles.totalsPanel}>
              <View style={styles.totalsRow}>
                <Text style={styles.totalsLabel}>Total Cost per Error</Text>
                <Text style={styles.totalsValue}>{formatCurrency(computed.total_cost)}</Text>
              </View>
              <View style={styles.totalsRow}>
                <Text style={styles.totalsLabel}>Error Cost per Day</Text>
                <Text style={styles.totalsValue}>{formatCurrency(computed.error_cost_per_day)}</Text>
              </View>
              <View style={styles.totalsRow}>
                <Text style={styles.totalsLabel}>Monthly Cost of Shipping Errors</Text>
                <Text style={[styles.totalsValue, styles.totalsValueCost]}>
                  {formatCurrency(computed.monthly_shipping)}
                </Text>
              </View>
              <View style={styles.totalsRow}>
                <Text style={styles.totalsLabel}>Monthly Cost of GroovePacker</Text>
                <Text style={styles.totalsValue}>{formatCurrency(computed.gp_cost)}</Text>
              </View>
              <View style={[styles.totalsRow, styles.totalsRowLast]}>
                <Text style={styles.totalsLabel}>Expected Savings per Month with GroovePacker</Text>
                <Text style={[styles.totalsValue, styles.totalsValueSaving]}>
                  {formatCurrency(computed.monthly_saving)}
                </Text>
              </View>
            </View>
          </View>

          {/* ─── Tell your team ───────────────────────────────────────── */}
          <View style={[styles.section, styles.teamPanel]}>
            <SectionHeader
              number="5"
              title="Tell Your Team"
              description="Send these results to yourself or a colleague so the numbers don't get lost after you close this tab."
            />
            <View style={styles.fieldRow}>
              <TextField
                testID="recipient_name"
                label="Your Name"
                value={recipientName}
                onChangeText={setRecipientName}
              />
              <TextField
                testID="recipient_one"
                label="Recipient Email 1"
                value={recipients[0]}
                onChangeText={(v) => handleRecipientChange(0, v)}
                keyboardType="email-address"
              />
              <TextField
                testID="recipient_two"
                label="Recipient Email 2"
                value={recipients[1]}
                onChangeText={(v) => handleRecipientChange(1, v)}
                keyboardType="email-address"
              />
              <TextField
                testID="recipient_three"
                label="Recipient Email 3"
                value={recipients[2]}
                onChangeText={(v) => handleRecipientChange(2, v)}
                keyboardType="email-address"
              />
            </View>
            <TextField
              testID="email_text"
              label="Message (optional)"
              value={emailText}
              onChangeText={setEmailText}
              multiline
            />
            <TouchableOpacity
              testID="followUpEmailCheckbox"
              style={styles.checkboxRow}
              onPress={() => setFollowUpEmail((prev) => !prev)}
            >
              <View style={[styles.checkbox, followUpEmail && styles.checkboxChecked]} />
              <Text style={styles.checkboxLabel}>
                Send me occasional special offers from GroovePacker
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              testID="sendButton"
              style={[
                styles.sendButton,
                submitStatus === 'pending' && styles.sendButtonDisabled
              ]}
              disabled={submitStatus === 'pending'}
              onPress={handleSend}
            >
              <Text style={styles.sendButtonText}>
                {submitStatus === 'pending' ? 'Sending…' : 'Send'}
              </Text>
            </TouchableOpacity>
            {submitStatus === 'failed' && submitError && (
              <Text style={[styles.inlineErrorText, {marginTop: 8}]}>
                {submitError.message}
              </Text>
            )}
          </View>

          {/* ─── CTAs ─────────────────────────────────────────────────── */}
          <View style={styles.ctaDivider} />
          <Text style={styles.ctaIntro}>Ready to stop losing money to fulfillment errors?</Text>
          <View style={styles.ctaRow}>
            <TouchableOpacity
              testID="seeDemoCta"
              style={[styles.ctaButton, styles.ctaSecondary]}
              onPress={() => openExternal(DEMO_URL)}
            >
              <Text style={styles.ctaSecondaryText}>See a Demo</Text>
            </TouchableOpacity>
            <TouchableOpacity
              testID="getStartedCta"
              style={[styles.ctaButton, styles.ctaPrimary]}
              onPress={() => openExternal(PRICING_URL)}
            >
              <Text style={styles.ctaPrimaryText}>Get Started</Text>
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>
      <Toast visible={showToast} message={toastMessage} onHide={hideToast} />
    </LinearGradient>
  );
};

export default CostCalculatorScreen;
